<template lang="pug">
.selector-tipo-documento
  .mb-3
    b-form-group(
      label="Tipo de Documento"
      label-for="tipo-documento-select"
    )
      b-form-select(
        id="tipo-documento-select"
        v-model="tipoDocumentoInterno"
        :options="opcionesTipoDocumento"
        @change="onTipoDocumentoChange"
        size="lg"
      )
  
  //- Sección de factura (solo visible si se selecciona factura)
  .factura-section(v-if="tipoDocumentoInterno === 'factura'")
    b-form-group(
      label="RUT Contribuyente"
      label-for="rut-contribuyente"
      description="Ingrese el RUT del contribuyente (se formateará automáticamente)"
    )
      b-input-group
        b-form-input(
          id="rut-contribuyente"
          v-model="rutContribuyenteFormateado"
          placeholder="12.345.678-9"
          :state="rutState"
          @blur="validarRUT"
        )
        b-input-group-append
          b-button(
            variant="primary"
            @click="validarRUT"
            :disabled="validando || !rutContribuyente"
          )
            b-spinner(v-if="validando" small)
            span(v-else) Validar
      
      //- Mensaje de error
      b-form-invalid-feedback(:state="rutState")
        | {{ rutError }}
      
      //- Mensaje de éxito
      b-form-valid-feedback(:state="rutState")
        | RUT válido
    
    //- Información del contribuyente (cuando está validado)
    b-card.mt-3(v-if="contribuyenteInfo" bg-variant="light")
      h6.mb-2 Información del Contribuyente
      .contribuyente-info
        .info-row
          strong RUT:
          span {{ contribuyenteInfo.rut }}
        .info-row
          strong Razón Social:
          span {{ contribuyenteInfo.razon_social }}
        .info-row(v-if="contribuyenteInfo.giro")
          strong Giro:
          span {{ contribuyenteInfo.giro }}
        .info-row(v-if="contribuyenteInfo.direccion")
          strong Dirección:
          span {{ contribuyenteInfo.direccion }}
        .info-row(v-if="contribuyenteInfo.comuna")
          strong Comuna:
          span {{ contribuyenteInfo.comuna }}
</template>

<script>
import axios from '@/plugins/axios'

export default {
  name: 'SelectorTipoDocumento',
  props: {
    // Tipo de documento inicial (por defecto 'boleta')
    tipoDocumento: {
      type: String,
      default: 'boleta',
      validator: (value) => ['boleta', 'factura'].includes(value)
    },
    // Si se debe deshabilitar el selector
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tipoDocumentoInterno: 'boleta',
      opcionesTipoDocumento: [
        { text: 'Boleta', value: 'boleta' },
        { text: 'Factura', value: 'factura' }
      ],
      // Datos de validación de RUT
      rutContribuyente: '',
      contribuyenteInfo: null,
      rutError: '',
      validando: false,
      rutValidado: false
    }
  },
  computed: {
    /**
     * Computed property para formatear el RUT automáticamente
     */
    rutContribuyenteFormateado: {
      get() {
        return this.rutContribuyente
      },
      set(valor) {
        // Limpiar el valor: eliminar puntos, espacios y dejar solo números, k, K y guión
        const rutLimpio = valor.replace(/[^0-9kK]/g, '')

        // Validar longitud mínima
        if (rutLimpio.length === 0) {
          this.rutContribuyente = ''
        } else if (rutLimpio.length === 1) {
          // Un solo carácter, no formatear aún
          this.rutContribuyente = rutLimpio
        } else {
          // Separar el cuerpo del dígito verificador
          const cuerpo = rutLimpio.slice(0, -1)
          const dv = rutLimpio.slice(-1).toUpperCase()

          // Formatear el cuerpo con puntos de miles (reverse para agregar desde el final)
          const cuerpoFormateado = cuerpo
            .split('')
            .reverse()
            .join('')
            .replace(/(\d{3})(?=\d)/g, '$1.')
            .split('')
            .reverse()
            .join('')

          // Construir el RUT formateado: número con puntos + guión + dígito verificador
          this.rutContribuyente = `${cuerpoFormateado}-${dv}`
        }

        // Notificar cambios
        this.onRutInput()
      }
    },
    /**
     * Estado del campo de RUT para validación de Bootstrap
     */
    rutState() {
      if (!this.rutContribuyente) return null
      if (this.validando) return null
      if (this.rutError) return false
      if (this.rutValidado) return true
      return null
    },
    /**
     * Si se puede continuar con el pago
     * - Para boleta: siempre true
     * - Para factura: solo si el RUT está validado
     */
    puedeIniciarPago() {
      if (this.tipoDocumentoInterno === 'boleta') return true
      if (this.tipoDocumentoInterno === 'factura') {
        return this.rutValidado && this.contribuyenteInfo !== null
      }
      return false
    }
  },
  watch: {
    tipoDocumento(newVal) {
      this.tipoDocumentoInterno = newVal
    },
    // Emitir cambios al padre
    puedeIniciarPago(newVal) {
      this.$emit('update:puede-iniciar-pago', newVal)
    }
  },
  methods: {
    /**
     * Validar RUT consultando OpenFactura
     */
    async validarRUT() {
      if (!this.rutContribuyente || this.rutContribuyente.trim() === '') {
        this.rutError = 'El RUT es requerido'
        this.rutValidado = false
        this.contribuyenteInfo = null
        return false
      }

      this.rutError = ''
      this.contribuyenteInfo = null
      this.validando = true
      this.rutValidado = false

      try {
        // Limpiar el RUT: eliminar puntos y espacios, dejar solo el guión
        const rutLimpio = this.rutContribuyente.replaceAll('.', '').trim()
        const response = await axios.get(`/api/oportunidadventa/contribuyente/${rutLimpio}/`)

        if (response.status === 200 && response.data.valido) {
          this.contribuyenteInfo = response.data
          this.rutValidado = true
          this.emitirDatos()
          return true
        }
        
        this.rutError = response.data.error || 'RUT inválido'
        this.rutValidado = false
        this.emitirDatos()
        return false
      } catch (error) {
        console.error('Error al validar RUT:', error)
        this.rutError = error.response?.data?.error || 'Error al validar RUT. Intente nuevamente.'
        this.rutValidado = false
        this.emitirDatos()
        return false
      } finally {
        this.validando = false
      }
    },
    
    /**
     * Limpiar datos de validación
     */
    limpiarValidacion() {
      this.rutContribuyente = ''
      this.contribuyenteInfo = null
      this.rutError = ''
      this.validando = false
      this.rutValidado = false
    },
    
    /**
     * Cuando cambia el tipo de documento
     */
    onTipoDocumentoChange(nuevoTipo) {
      console.log('Tipo de documento cambiado a:', nuevoTipo)
      this.tipoDocumentoInterno = nuevoTipo
      
      // Si cambia a boleta, limpiar validación
      if (nuevoTipo === 'boleta') {
        this.limpiarValidacion()
      }
      
      // Emitir al padre
      this.$emit('update:tipo-documento', nuevoTipo)
      this.emitirDatos()
    },
    /**
     * Cuando se escribe en el campo de RUT
     */
    onRutInput() {
      // Resetear validación cuando se modifica el RUT
      if (this.rutValidado) {
        this.rutValidado = false
        this.contribuyenteInfo = null
      }
      this.emitirDatos()
    },
    /**
     * Emitir datos completos al padre
     */
    emitirDatos() {
      // Limpiar el RUT de puntos antes de enviarlo
      const rutLimpio = this.rutContribuyente ? this.rutContribuyente.replaceAll('.', '') : null
      
      const datos = {
        tipo_documento: this.tipoDocumentoInterno,
        rut_contribuyente: this.tipoDocumentoInterno === 'factura' ? rutLimpio : null,
        valido: this.puedeIniciarPago,
        contribuyente_info: this.contribuyenteInfo
      }
      console.log('Emitiendo datos al padre:', datos)
      this.$emit('datos-actualizados', datos)
    }
  },
  mounted() {
    console.log('SelectorTipoDocumento montado')
    console.log('tipoDocumentoInterno inicial:', this.tipoDocumentoInterno)
    console.log('puedeIniciarPago inicial:', this.puedeIniciarPago)
    
    // Emitir estado inicial
    this.$emit('update:puede-iniciar-pago', this.puedeIniciarPago)
    this.emitirDatos()
  }
}
</script>

<style lang="scss" scoped>
.selector-tipo-documento {
  .factura-section {
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 0.25rem;
  }

  .contribuyente-info {
    .info-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      
      strong {
        margin-right: 1rem;
        min-width: 120px;
      }
      
      span {
        text-align: right;
        flex: 1;
      }
    }
  }
}
</style>
